import {
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  PageContainer,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, MessageCircle, Pencil, Target, Trash2, Trophy, Users } from 'lucide-react';
import { useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { TeamEditDialog } from '@/components/teams/TeamEditDialog';
import { useQrDataUrl } from '@/hooks/useQrDataUrl';

export interface TeamDetail {
  id: string;
  eventId: string;
  name: string;
  code: string;
  iconName: string;
  iconColor: string;
  photoUrl: string | null;
  isStaff: boolean;
  totalScore?: number;
  challengesCompleted?: number;
  memberCount?: number;
  allianceId?: string | null;
  alliance?: { id: string; name: string; color: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

interface EventLite {
  id: string;
  name: string;
}

const tabs = [
  { label: 'Overview', path: 'overview', icon: Trophy },
  { label: 'Members', path: 'members', icon: Users },
  { label: 'Challenges', path: 'challenges', icon: Target },
  { label: 'Activity', path: 'activity', icon: Activity },
  { label: 'Chat', path: 'chat', icon: MessageCircle },
] as const;

/**
 * Top-level team detail screen. Reuses the shared `DetailPageHero`
 * shell from the frontend package so the hero, breadcrumbs, and
 * sticky tab bar are visually identical to the event detail page.
 * The QR code becomes the hero image — encoded as a PNG data URL
 * via `useQrDataUrl` so it slots straight into the existing image
 * prop without a custom hero variant.
 */
export function EventTeamDetailPage() {
  const { id: eventId, teamId } = useParams<{ id: string; teamId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const teamQuery = useQuery<{ success: boolean; data: TeamDetail }>({
    queryKey: ['EventTeam', eventId, teamId],
    queryFn: () => apiClient.get(`/events/${eventId}/teams/${teamId}`),
    enabled: !!eventId && !!teamId,
  });
  const team = teamQuery.data?.data;

  // Pull the parent event just for the breadcrumb label — the rest
  // of the page is fed by the team query above.
  const eventQuery = useQuery<{ success: boolean; data: EventLite }>({
    queryKey: ['Event', eventId],
    queryFn: () => apiClient.get(`/events/${eventId}`),
    enabled: !!eventId,
  });
  const event = eventQuery.data?.data;

  const qrDataUrl = useQrDataUrl(team?.code, 320);

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete(`/events/${eventId}/teams/${teamId}`),
    onSuccess: () => {
      toast.success('Team deleted');
      queryClient.invalidateQueries({ queryKey: ['EventTeams', eventId] });
      navigate(`/events/${eventId}/teams`);
    },
    onError: (err: Error) => toast.error(err?.message || 'Failed to delete team'),
  });

  const basePath = `/events/${eventId}/teams/${teamId}`;

  return (
    // `padding="none"` + flex column matches the event detail page
    // shell so the breadcrumb row sits flush against the blue hero
    // (no surrounding p-6 gap).
    <PageContainer padding="none" className="flex flex-col overflow-hidden">
      <DetailPageHeader
        actions={
          team ? (
            <>
              <Button variant="outline" onClick={() => setEditOpen(true)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : null
        }>
        <Breadcrumbs
          items={[
            { label: 'Home', href: '/' },
            { label: 'Events', href: '/events' },
            { label: event?.name || 'Event', href: `/events/${eventId}/overview` },
            { label: 'Teams', href: `/events/${eventId}/teams` },
            { label: team?.name || 'Team', current: true },
          ]}
        />
      </DetailPageHeader>

      <DetailPageHero
        isLoading={teamQuery.isLoading}
        category={{ label: 'Team', icon: Users }}
        title={team?.name || 'Team'}
        image={{
          url: qrDataUrl,
          alt: team ? `QR code for team code ${team.code}` : 'Team QR code',
          fallbackIcon: Users,
          // QR is intrinsically 1:1 — the square hero slot fits it edge-
          // to-edge so the code stays scannable without left/right
          // letterboxing. `contain` is the safety net for non-square
          // assets the slot might receive.
          shape: 'square',
          objectFit: 'contain',
        }}
        meta={
          team ? (
            <span className="text-sm text-white/70">
              {team.memberCount ?? 0} members • Code{' '}
              <code className="rounded bg-white/10 px-1.5 py-0.5 font-mono">{team.code}</code>
            </span>
          ) : undefined
        }
        badges={
          team ? (
            <>
              {team.isStaff && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                  Staff
                </span>
              )}
              {team.alliance && (
                <span
                  className="rounded-full px-2 py-0.5 text-xs font-medium text-white"
                  style={
                    team.alliance.color ? { backgroundColor: team.alliance.color } : undefined
                  }>
                  {team.alliance.name}
                </span>
              )}
            </>
          ) : undefined
        }
        tabs={[...tabs]}
        basePath={basePath}>
        {/* Children read `team` from outlet context — gate the Outlet
            on team being loaded so we never pass `team: undefined`
            into a child that immediately dereferences it. */}
        {team ? (
          <Outlet context={{ team, eventId, teamId }} />
        ) : (
          <div className="px-6 py-8 text-sm text-muted-foreground">Loading team…</div>
        )}
      </DetailPageHero>

      {team && (
        <TeamEditDialog
          eventId={eventId!}
          team={team}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete team?"
        description={`This will permanently delete "${team?.name}" and all of its data.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
      />
    </PageContainer>
  );
}
